import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSelector } from '@/components/LanguageSelector';
import { detectLanguageByIP } from '@/i18n';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Building2,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import PhoneNumberInput, { isValidPhone } from '@/components/PhoneNumberInput';

const API = process.env.REACT_APP_BACKEND_URL;

// Validação de CPF
const validateCPF = (cpf) => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let digit1 = (sum * 10) % 11;
  if (digit1 === 10) digit1 = 0;
  if (digit1 !== parseInt(cleaned[9])) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  let digit2 = (sum * 10) % 11;
  if (digit2 === 10) digit2 = 0;
  if (digit2 !== parseInt(cleaned[10])) return false;
  
  return true;
};

// Formatar CPF
const formatCPF = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
};

const SignUpPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    cpf: '',
    email: '',
    phone: '',
    birth_date: '',
    department_id: '',
    password: '',
    confirm_password: '',
  });
  
  const [errors, setErrors] = useState({});

  // Detectar idioma por IP na primeira visita
  useEffect(() => {
    detectLanguageByIP();
  }, []);
  
  // Carregar departamentos
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/departments`);
        setDepartments(response.data);
      } catch (error) {
        console.error('Erro ao carregar departamentos:', error);
      }
    };
    fetchDepartments();
  }, []);
  
  const handleChange = (field, value) => {
    let processedValue = value;
    
    // Normalizar nome para maiúsculas
    if (field === 'full_name') {
      processedValue = value.toUpperCase();
    }
    
    // Formatar CPF
    if (field === 'cpf') {
      processedValue = formatCPF(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    
    // Limpar erro do campo ao editar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    // Nome
    if (!formData.full_name || formData.full_name.trim().length < 3) {
      newErrors.full_name = t('nameMinLength');
    }
    
    // CPF
    if (!validateCPF(formData.cpf)) {
      newErrors.cpf = t('invalidCpf');
    }
    
    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }
    
    // Telefone
    if (!isValidPhone(formData.phone)) {
      newErrors.phone = t('invalidPhone');
    }
    
    // Data de nascimento
    if (!formData.birth_date) {
      newErrors.birth_date = t('birthDateRequired');
    } else {
      const birth = new Date(formData.birth_date);
      const today = new Date();
      const age = today.getFullYear() - birth.getFullYear();
      if (age < 16) {
        newErrors.birth_date = t('minAge');
      }
    }
    
    // Departamento
    if (!formData.department_id) {
      newErrors.department_id = t('selectDepartmentError');
    }
    
    // Senha
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = t('passwordMinChars');
    }
    
    // Confirmar senha
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = t('passwordsDontMatch');
    }
    
    // Termos
    if (!acceptTerms) {
      newErrors.terms = t('acceptTermsError');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/auth/signup`, {
        full_name: formData.full_name,
        cpf: formData.cpf.replace(/\D/g, ''),
        email: formData.email.toLowerCase(),
        phone: formData.phone,
        birth_date: formData.birth_date,
        department_id: formData.department_id,
        password: formData.password,
        language: i18n.language?.split('-')[0] || 'pt',
      });
      
      setSuccess(true);
      toast.success(t('signUpSuccess'));
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao enviar cadastro';
      toast.error(message);
      
      // Mostrar erro específico no campo
      if (message.toLowerCase().includes('cpf')) {
        setErrors(prev => ({ ...prev, cpf: t('cpfAlreadyExists') }));
      } else if (message.toLowerCase().includes('email') || message.toLowerCase().includes('e-mail')) {
        setErrors(prev => ({ ...prev, email: t('emailAlreadyExists') }));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Tela de sucesso
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        {/* Language Selector - Top Right */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSelector variant="flags-only" />
        </div>
        
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('signUpSuccess')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('signUpSuccessMessage')}
            </p>
            <Button onClick={() => navigate('/login')} className="w-full" data-testid="back-to-login-btn">
              {t('backToLoginBtn')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector variant="flags-only" />
      </div>
      
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t('signUp')}</CardTitle>
          <CardDescription>
            {t('signUpSubtitle')}
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* Nome Completo */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('fullName')} *
              </Label>
              <Input
                id="full_name"
                data-testid="signup-fullname-input"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder={t('fullName').toUpperCase()}
                className={errors.full_name ? 'border-red-500' : ''}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.full_name && (
                <p className="text-sm text-red-500">{errors.full_name}</p>
              )}
            </div>
            
            {/* CPF e Data de Nascimento */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cpf" className="flex items-center gap-2">
                  {t('cpf')} *
                </Label>
                <Input
                  id="cpf"
                  data-testid="signup-cpf-input"
                  value={formData.cpf}
                  onChange={(e) => handleChange('cpf', e.target.value)}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  className={errors.cpf ? 'border-red-500' : ''}
                />
                {errors.cpf && (
                  <p className="text-sm text-red-500">{errors.cpf}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="birth_date" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('birthDate')} *
                </Label>
                <Input
                  id="birth_date"
                  data-testid="signup-birthdate-input"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleChange('birth_date', e.target.value)}
                  className={errors.birth_date ? 'border-red-500' : ''}
                />
                {errors.birth_date && (
                  <p className="text-sm text-red-500">{errors.birth_date}</p>
                )}
              </div>
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t('email')} *
              </Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="seu@email.com"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>
            
            {/* Telefone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('phone')} *
              </Label>
              <div className={errors.phone ? 'rounded-md border border-red-500 p-2' : ''}>
                <PhoneNumberInput
                  value={formData.phone}
                  onChange={(phone) => handleChange('phone', phone)}
                  idPrefix="signup-phone"
                />
              </div>
              <Input
                type="hidden"
                id="phone"
                data-testid="signup-phone-input"
                value={formData.phone}
                readOnly
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
            
            {/* Departamento */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {t('department')} *
              </Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => handleChange('department_id', value)}
                data-testid="signup-department-select"
              >
                <SelectTrigger className={errors.department_id ? 'border-red-500' : ''} data-testid="signup-department-trigger">
                  <SelectValue placeholder={t('selectDepartment')} />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id} data-testid={`dept-option-${dept.id}`}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.department_id && (
                <p className="text-sm text-red-500">{errors.department_id}</p>
              )}
            </div>
            
            {/* Senha */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {t('password')} *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="signup-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="••••••"
                    className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">
                  {t('confirmPasswordLabel')} *
                </Label>
                <Input
                  id="confirm_password"
                  data-testid="signup-confirm-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={(e) => handleChange('confirm_password', e.target.value)}
                  placeholder="••••••"
                  className={errors.confirm_password ? 'border-red-500' : ''}
                />
                {errors.confirm_password && (
                  <p className="text-sm text-red-500">{errors.confirm_password}</p>
                )}
              </div>
            </div>
            
            {/* Termos */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                data-testid="signup-terms-checkbox"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground">
                {t('termsAccept')}
              </label>
            </div>
            {errors.terms && (
              <p className="text-sm text-red-500">{errors.terms}</p>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sending')}
                </>
              ) : (
                t('requestSignUp')
              )}
            </Button>
            
            <div className="text-center text-sm">
              <Link
                to="/login"
                className="text-primary hover:underline flex items-center justify-center gap-1"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('alreadyHaveAccount')}
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default SignUpPage;
